import React, { useRef, useEffect, useState } from "react";
import { DatePicker } from "@rio-cloud/rio-uikit";
import Card from "@rio-cloud/rio-uikit/Card";
import moment, { Moment } from "moment";
import { useIntl } from "react-intl";
import { useAppDispatch } from "@store";
import { setEndDate, setStartDate } from "@store/date/dateSlice";
import { useT } from "@hooks";

interface DateRangePickerProps {
  show: boolean;
  onClose: () => void;
  startDate?: string;
  endDate?: string;
  onStartDateChange?: (date: string) => void;
  onEndDateChange?: (date: string) => void;
}

const resolveMomentLocale = (currentLocale: string) => {
  if (currentLocale.startsWith("en")) return "en-gb";
  if (currentLocale.startsWith("es")) return "es";
  return "pt-br";
};

export default function DateRangePicker({
  show,
  onClose,
  startDate,
  endDate,
  onStartDateChange,
  onEndDateChange,
}: Readonly<DateRangePickerProps>) {
  const t = useT();
  const { locale } = useIntl();
  const momentLocale = resolveMomentLocale(locale);
  moment.locale(momentLocale);
  const dispatch = useAppDispatch();
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        const target = event.target as HTMLElement;
        const monthNavigation = target.closest(".month-navigation");
        if (!monthNavigation) {
          onClose();
        }
      }
    };

    if (show) {
      setTimeout(() => {
        document.addEventListener("click", handleClickOutside);
      }, 0);
    }

    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, [show, onClose]);

  const handleStartDateChange = (result: Moment | string) => {
    const date = moment(result).format("YYYY-MM-DD");
    dispatch(setStartDate(date));

    if (endDate && moment(endDate).isBefore(date)) {
      dispatch(setEndDate(""));
    }
  };

  const handleEndDateChange = (result: Moment | string) => {
    const date = moment(result).format("YYYY-MM-DD");
    if (startDate && moment(date).isBefore(moment(startDate, "YYYY-MM-DD"))) {
      dispatch(setEndDate(""));
    } else {
      dispatch(setEndDate(date));
      if (startDate && date) {
        onClose();
      }
    }
  };

  // Calcular datas válidas com base no limite de 1 ano
  const getValidDateProps = (isEndDatePicker: boolean) => {
    if (isEndDatePicker && startDate) {
      const minDate = moment(startDate);
      const maxDate = moment(startDate).add(1, "year");
      return {
        isValidDate: (current: Moment) => {
          return current.isSameOrAfter(minDate, "day") && current.isSameOrBefore(maxDate, "day");
        },
      };
    }

    if (!isEndDatePicker && endDate) {
      const minDate = moment(endDate).subtract(1, "year");
      const maxDate = moment(endDate);
      return {
        isValidDate: (current: Moment) => {
          return current.isSameOrAfter(minDate, "day") && current.isSameOrBefore(maxDate, "day");
        },
      };
    }

    return {};
  };

  const formatDateForDisplay = (date?: string) => {
    if (!date) return "";
    return moment(date, "YYYY-MM-DD").format("DD/MM/YYYY");
  };

  const [inputStartDate, setInputStartDate] = useState<Moment | string>(formatDateForDisplay(startDate));
  const [inputEndDate, setInputEndDate] = useState<Moment | string>(formatDateForDisplay(endDate));

  useEffect(() => {
    setInputStartDate(formatDateForDisplay(startDate));
  }, [startDate]);

  useEffect(() => {
    setInputEndDate(formatDateForDisplay(endDate));
  }, [endDate]);

  const onChangeStartDate = (result: Moment | string, isValid: boolean) => {
    if (moment.isMoment(result)) {
      setInputStartDate(result);
    } else {
      if (result.length > 10) {
        return;
      }

      setInputStartDate(result);
    }

    if (isValid) {
      handleStartDateChange(result);
    }
  };

  const onChangeEndDate = (result: Moment | string, isValid: boolean) => {
    if (moment.isMoment(result)) {
      setInputEndDate(result);
    } else {
      if (result.length > 10) {
        return;
      }

      setInputEndDate(result);
    }

    if (isValid) {
      handleEndDateChange(result);
    }
  };

  if (!show) return null;

  return (
    <div
      className="width-100pct"
      ref={containerRef}
      role="none"
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.stopPropagation();
        }
      }}
    >
      <Card className="padding-25 rounded display-flex flex-column gap-20 width-100pct shadow-default bg-lightest">
        <div className="display-flex flex-row width-100pct gap-20">
          <div className="form-group flex-1-1 margin-0">
            <label className="text-size-14 text-color-black text-medium" htmlFor="start-date-picker">
              {t("dashboard.datepicker.startPeriod")}
            </label>
            <div className="input-group">
              <span className="input-group-addon">
                <i className="rioglyph rioglyph-calendar" />
              </span>
              <DatePicker
                timeFormat={false}
                id="start-date-picker"
                dateFormat="DD/MM/YYYY"
                locale={momentLocale}
                className="width-100pct margin-0"
                inputProps={{ placeholder: t("dashboard.datepicker.placeholder") }}
                onChange={onChangeStartDate}
                value={inputStartDate}
                closeOnClickOutside={false}
                initialViewMode="days"
                {...getValidDateProps(false)}
              />
            </div>
          </div>

          <div className="form-group flex-1-1 margin-0">
            <label className="text-size-14 text-color-black text-medium" htmlFor="end-date-picker">
              {t("dashboard.datepicker.endPeriod")}
            </label>
            <div className="input-group">
              <span className="input-group-addon">
                <i className="rioglyph rioglyph-calendar" />
              </span>
              <DatePicker
                timeFormat={false}
                id="end-date-picker"
                dateFormat="DD/MM/YYYY"
                locale={momentLocale}
                className="width-100pct margin-0"
                inputProps={{ placeholder: t("dashboard.datepicker.placeholder") }}
                onChange={onChangeEndDate}
                value={inputEndDate}
                closeOnClickOutside={false}
                initialViewMode="days"
                {...getValidDateProps(true)}
              />
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
}
